import { useState, useRef, useEffect } from 'react';
import { UserActions } from 'BMapsCmds';
import { BackgroundColors } from '../themes';
import { useColorSchemeInfo } from '../redux/prefs/access';
import BMapsTooltip from './BMapsTooltip';

// Circle geometry mirrors the CSS (.backgroundColorButton): 4vh diameter,
// 0.6vh gap. Plus a little breathing room each side before we collapse.
const CIRCLE_VH = 4;
const GAP_VH = 0.6;
const SIDE_MARGIN_VH = 2;

/**
 * Background-color picker.
 *
 * Shows the full horizontal row of color circles whenever it fits, centered at
 * the viewer bottom. Only when the row can no longer fit between the InfoDisplay
 * inspector (left) and the right control/side panels does it collapse to a
 * single circle (the current color, with the magenta selected ring); clicking
 * that expands a vertical color column that collapses again on select or
 * click-outside. This keeps the swatches from overlapping the InfoDisplay tabs
 * when the side panels narrow the viewer.
 */
export default function BackgroundButtons() {
    const curColorInfo = useColorSchemeInfo();
    const colorNames = Object.keys(BackgroundColors);
    const [collapsed, setCollapsed] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const containerRef = useRef(null);

    // Collapse only when the horizontal row no longer fits the clear gap between
    // the inspector (#info_display) and the right panels (#rightsidepanel).
    // Re-measured whenever either side resizes (panel open/resize) or the window
    // resizes.
    useEffect(() => {
        function recompute() {
            const info = document.getElementById('info_display');
            const right = document.getElementById('rightsidepanel');
            const infoRight = info ? info.getBoundingClientRect().right : 0;
            const rightLeft = right
                ? right.getBoundingClientRect().left : window.innerWidth;
            const availablePx = rightLeft - infoRight;
            const vh = window.innerHeight / 100;
            const rowVh = colorNames.length * CIRCLE_VH
                + (colorNames.length - 1) * GAP_VH
                + 2 * SIDE_MARGIN_VH;
            setCollapsed(availablePx < rowVh * vh);
        }
        recompute();
        const ro = new ResizeObserver(recompute);
        const info = document.getElementById('info_display');
        const right = document.getElementById('rightsidepanel');
        if (info) ro.observe(info);
        if (right) ro.observe(right);
        window.addEventListener('resize', recompute);
        return () => {
            ro.disconnect();
            window.removeEventListener('resize', recompute);
        };
    }, [colorNames.length]);

    // When space returns, drop any open vertical selector.
    useEffect(() => {
        if (!collapsed) setExpanded(false);
    }, [collapsed]);

    // Collapse the open vertical selector on an outside click.
    useEffect(() => {
        if (!expanded) return undefined;
        function handleOutside(e) {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setExpanded(false);
            }
        }
        document.addEventListener('mousedown', handleOutside);
        return () => document.removeEventListener('mousedown', handleOutside);
    }, [expanded]);

    const curColor = curColorInfo.name;

    function handleSelect(color) {
        UserActions.SetColorScheme(color);
        setExpanded(false);
    }

    // Collapsed, unopened: a single circle (current color, magenta ring).
    if (collapsed && !expanded) {
        return (
            <div id="backgroundColorButtons" className="collapsed" ref={containerRef}>
                <BMapsTooltip placement="left" useColorScheme title="Set background color">
                    <button
                        type="button"
                        className="backgroundColorButton selected"
                        style={{ background: BackgroundColors[curColor]?.css }}
                        onClick={() => setExpanded(true)}
                        aria-label="Set background color"
                    >
                        &nbsp;
                    </button>
                </BMapsTooltip>
            </div>
        );
    }

    const buttons = colorNames.map((color) => (
        <BackgroundButton
            color={color}
            curColor={curColor}
            onSelect={handleSelect}
            key={color}
        />
    ));

    // Collapsed + opened: vertical column expanding upward.
    if (collapsed) {
        return (
            <div id="backgroundColorButtons" className="expanded" ref={containerRef}>
                {buttons}
            </div>
        );
    }

    // Default: the full horizontal row.
    return (
        <BMapsTooltip placement="top" useColorScheme title="Set background color">
            <div id="backgroundColorButtons" className="row" ref={containerRef}>
                {buttons}
            </div>
        </BMapsTooltip>
    );
}

function BackgroundButton({ color, curColor, onSelect }) {
    return (
        <button
            name={color}
            className={`backgroundColorButton ${color === curColor ? 'selected' : ''}`}
            type="button"
            style={{
                background: BackgroundColors[color].css,
            }}
            onClick={() => onSelect(color)}
        >
            &nbsp;
        </button>
    );
}
